import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { GridCellParams, GridRowsProp, GridColDef } from "@mui/x-data-grid";
import { Box, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CVData from "../../../../types/CVData";
import DeleteIcon from "@mui/icons-material/Delete";
import { FilterPositionInput } from "../filters/positionFilter";
import { FilterSkillInput } from "../filters/skillFilter";
import { FilterCourseInput } from "../filters/courseFilter";
import { FilterTypeInput } from "../filters/typeFilter";
import { FilterStatusInput } from "../filters/statusFilter";
import createStringOperators from "../filters/createStringOperators";

function renderStatus(status: "active" | "inactive") {
  const colors: { [index: string]: "success" | "default" } = {
    active: "success",
    inactive: "default",
  };

  return <Chip label={status} color={colors[status]} size="small" />;
}

export function renderAvatar(
  params: GridCellParams<{ name: string; color: string }, any, any>
) {
  if (params.value == null) {
    return "";
  }

  return (
    <Avatar
      sx={{
        backgroundColor: params.value.color,
        width: "24px",
        height: "24px",
        fontSize: "0.85rem",
      }}
    >
      {params.value.name.toUpperCase().substring(0, 1)}
    </Avatar>
  );
}

export function getColumns(
  navigate: (path: string, options?: any) => void,
  handleDeleteClick: (arg: any) => void
): GridColDef[] {
  return [
    {
      field: "cvUId",
      headerName: "cvUId",
      filterable: true,
      sortable: false,
      minWidth: 150,
    },
    {
      field: "Edit Button",
      headerName: "",
      width: 32,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          color="primary"
          sx={{ padding: 0, margin: 0, height: "24px", width: "24px" }}
          onClick={() =>
            navigate("/create-cv", {
              state: { user: params.row },
            })
          }
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: "name",
      headerName: "Name and Surname",
      flex: 1.5,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.value}
          {params.row.isCurrentUser && (
            <Chip label="You" color="primary" size="small" />
          )}
        </Box>
      ),
    },
    {
      field: "position",
      headerName: "Position",
      flex: 1.5,
      minWidth: 100,
      filterOperators: createStringOperators(FilterPositionInput),
    },
    {
      field: "skills",
      headerName: "Skills",
      flex: 2,
      renderCell: (params) => {
        const skillGroups = Array.isArray(params.value) ? params.value : [];

        // Normalize
        const skillNames = skillGroups
          .flat()
          .map((s) => (typeof s === "string" ? s : s?.name))
          .filter(Boolean);

        return skillNames.length ? skillNames.join(", ") : "-";
      },
      filterOperators: createStringOperators(FilterSkillInput),
    },
    {
      field: "certificates",
      headerName: "Certificates",
      headerAlign: "left",
      align: "left",
      minWidth: 300,
      filterOperators: createStringOperators(FilterCourseInput),
    },
    {
      field: "type",
      headerName: "Type",
      headerAlign: "left",
      align: "left",
      minWidth: 100,
      filterOperators: createStringOperators(FilterTypeInput),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 80,
      renderCell: (params) => renderStatus(params.value as any),
      filterOperators: createStringOperators(FilterStatusInput),
    },
    {
      field: "Delete Button",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 32,
      renderCell: (params) => (
        <IconButton
          color="error"
          sx={{ padding: 0, margin: 0, height: "24px", width: "24px" }}
          onClick={() => handleDeleteClick(params.row)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
}

export function formatCVForTable(users: CVData[]): GridRowsProp {
  const loggedInEmail = localStorage.getItem("email");
  const list = users ?? [];

  return list.map((cv) => ({
    cvUId: cv.cvUId,
    id: cv.cvId,
    name: cv.name,
    position: cv.position.name,
    skills: Object.values(cv.skills),
    certificates: cv.certifications?.map((cer) => cer.course),
    type: cv.type,
    status: cv.status,
    isCurrentUser: cv.cvId === loggedInEmail,
  }));
}
